// Analytics Dashboard Component
import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Users, 
  Eye, 
  FileText, 
  TrendingUp,
  Calendar,
  Package,
  MessageSquare,
  Mail,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/analytics/summary`, {
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
        setError(null);
      } else {
        setError('Erreur lors du chargement des statistiques');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={fetchAnalytics}>Réessayer</Button>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, subValue, color }) => (
    <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="text-sm text-slate-500">{label}</span>
      </div>
      <p className="text-3xl font-bold text-slate-800">{value}</p>
      {subValue && <p className="text-sm text-slate-400 mt-1">{subValue}</p>}
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-slate-800">Statistiques</h2>
          <p className="text-slate-500">Vue d'ensemble de l'activité du site</p>
        </div>
        <Button variant="outline" onClick={fetchAnalytics} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={Eye} 
          label="Vues aujourd'hui" 
          value={data?.page_views?.today || 0}
          subValue={`${data?.page_views?.week || 0} cette semaine`}
          color="bg-blue-500"
        />
        <StatCard 
          icon={Users} 
          label="Visiteurs aujourd'hui" 
          value={data?.visitors?.today || 0}
          subValue={`${data?.visitors?.week || 0} cette semaine`}
          color="bg-green-500"
        />
        <StatCard 
          icon={Eye} 
          label="Vues ce mois" 
          value={data?.page_views?.month || 0}
          subValue={`${data?.visitors?.month || 0} visiteurs`}
          color="bg-purple-500"
        />
        <StatCard 
          icon={TrendingUp} 
          label="Vues cette année" 
          value={data?.page_views?.year || 0}
          subValue={`${data?.visitors?.year || 0} visiteurs`}
          color="bg-orange-500"
        />
      </div>

      {/* Forms Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={Mail} 
          label="Messages (total)" 
          value={data?.forms?.messages_total || 0}
          subValue={`${data?.forms?.messages_week || 0} cette semaine`}
          color="bg-pink-500"
        />
        <StatCard 
          icon={Mail} 
          label="Messages (mois)" 
          value={data?.forms?.messages_month || 0}
          color="bg-pink-400"
        />
        <StatCard 
          icon={MessageSquare} 
          label="Demandes (total)" 
          value={data?.forms?.feedbacks_total || 0}
          subValue={`${data?.forms?.feedbacks_week || 0} cette semaine`}
          color="bg-teal-500"
        />
        <StatCard 
          icon={MessageSquare} 
          label="Demandes (mois)" 
          value={data?.forms?.feedbacks_month || 0}
          color="bg-teal-400"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Views Chart */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            Vues des 7 derniers jours
          </h3>
          <div className="flex items-end justify-between h-40 gap-2">
            {data?.daily_views?.map((day, index) => {
              const maxViews = Math.max(...data.daily_views.map(d => d.views), 1);
              const height = (day.views / maxViews) * 100;
              const dayName = new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short' });
              return (
                <div key={index} className="flex flex-col items-center flex-1">
                  <span className="text-xs text-slate-500 mb-1">{day.views}</span>
                  <div 
                    className="w-full bg-blue-500 rounded-t-md transition-all hover:bg-blue-600"
                    style={{ height: `${Math.max(height, 5)}%` }}
                  />
                  <span className="text-xs text-slate-400 mt-2">{dayName}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-500" />
            Pages les plus visitées
          </h3>
          <div className="space-y-3">
            {data?.top_pages?.length > 0 ? (
              data.top_pages.slice(0, 5).map((page, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs flex items-center justify-center font-medium">
                      {index + 1}
                    </span>
                    <div>
                      <span className="text-sm text-slate-700 block">
                        {page.title && page.title !== 'SAE' ? page.title : page.path}
                      </span>
                      {page.title && page.title !== 'SAE' && page.title !== page.path && (
                        <span className="text-xs text-slate-400">{page.path}</span>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium text-slate-500">{page.views} vues</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">Aucune donnée disponible</p>
            )}
          </div>
        </div>
      </div>

      {/* Equipment Stats */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-green-500" />
          Équipements les plus consultés
        </h3>
        {data?.top_equipment?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.top_equipment.slice(0, 6).map((eq, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {eq.equipment_name || `Équipement ${eq.equipment_id}`}
                  </p>
                  <p className="text-xs text-slate-500">{eq.views} consultations</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-4">
            Aucune consultation d'équipement enregistrée
          </p>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <strong>💡 Note :</strong> Les statistiques se mettent à jour en temps réel. 
        Le tracking des visiteurs est anonyme et respecte la vie privée (pas de cookies tiers).
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
